import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { newPasswordSchema } from '@lib/input/tenant-auth.input'
import { linkTokenParamSchema } from '@lib/validators/params'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState, LoadingState } from '@/components/PageState'
import { ROLE_LABELS } from '@/components/team/roles'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field, fieldMessageId } from '@/components/ui/field'
import { isApiError } from '@/helpers/apiClient'
import { apiErrorMessage, apiFieldErrors } from '@/helpers/apiErrorMessage'
import { invitationKeys } from '@/hooks/queryKeys'
import { tenantAuthService } from '@/services/TenantAuthService'

/**
 * Accept an emailed invitation. Someone with an existing dashboard login
 * confirms with their password; anyone else creates their login here.
 */
export function AcceptInvitePage() {
  const { token: rawToken } = useParams()
  const parsedToken = linkTokenParamSchema.safeParse(rawToken)
  const token = parsedToken.success ? parsedToken.data : null
  const { acceptInvitation, setActiveTenant } = useTenantAuth()
  const navigate = useNavigate()
  const preview = useQuery({
    queryKey: invitationKeys.preview(token),
    queryFn: () => {
      if (token === null) throw new Error('Invitation link is not valid')
      return tenantAuthService.previewInvitation(token)
    },
    enabled: token !== null,
    retry: false,
  })
  const [name, setName] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [fieldErrors, setFieldErrors] = React.useState<Partial<Record<'name' | 'password', string>>>({})
  const [error, setError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  if (preview.isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-canvas">
        <LoadingState />
      </div>
    )
  }
  // A dead invitation answers 404. Anything else (offline, a server error) is worth another try.
  if (preview.isError && !(isApiError(preview.error) && preview.error.status === 404)) {
    return (
      <AuthShell title="Invitation">
        <ErrorState
          message={apiErrorMessage(preview.error, 'Could not load this invitation.')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void preview.refetch()}>
              Try again
            </Button>
          }
        />
      </AuthShell>
    )
  }
  if (token === null || preview.isError || !preview.data) {
    return (
      <AuthShell title="Invitation unavailable">
        <p className="text-body text-muted-foreground">
          This invitation link is invalid, has expired, or was already used. Ask the person who
          invited you to send a new one.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </AuthShell>
    )
  }

  const invitation = preview.data

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    if (!invitation.accountExists) {
      if (name.trim().length === 0) {
        setFieldErrors({ name: 'Enter your name.' })
        return
      }
      const parsed = newPasswordSchema.safeParse(password)
      if (!parsed.success) {
        setFieldErrors({ password: parsed.error.issues[0]?.message ?? 'Choose a longer password.' })
        return
      }
    }
    if (token === null) return
    setIsSubmitting(true)
    try {
      await acceptInvitation(
        token,
        invitation.accountExists ? { password } : { password, name: name.trim() },
      )
      // Land in the organization they just joined, not whichever they used last.
      setActiveTenant(invitation.tenantId)
      navigate('/envelopes', { replace: true })
    } catch (failure) {
      if (isApiError(failure) && failure.body.code === 'auth.invalid_credentials') {
        setFieldErrors({ password: 'That password is not correct for this account.' })
        return
      }
      const fields = apiFieldErrors(failure, ['name', 'password'])
      if (fields) setFieldErrors(fields)
      else setError(apiErrorMessage(failure, 'Could not accept the invitation.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell
      title={`Join ${invitation.tenantName}`}
      description={
        <>
          You&apos;re invited as <strong>{ROLE_LABELS[invitation.role]}</strong> for{' '}
          <strong>{invitation.email}</strong>.
          {invitation.accountExists
            ? ' Confirm with your existing PixelSign password.'
            : ' Create your dashboard login to accept.'}
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        {invitation.accountExists ? null : (
          <Field label="Your name" htmlFor="name" error={fieldErrors.name}>
            <Input
              id="name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={fieldErrors.name ? true : undefined}
              aria-describedby={fieldErrors.name ? fieldMessageId('name') : undefined}
            />
          </Field>
        )}
        <Field
          label={invitation.accountExists ? 'Password' : 'Choose a password'}
          htmlFor="password"
          error={fieldErrors.password}
          {...(invitation.accountExists
            ? {
                labelAside: (
                  <Link to="/forgot-password" className="text-caption text-link underline-offset-4 hover:underline">
                    Forgot your password?
                  </Link>
                ),
              }
            : { hint: 'At least 12 characters.' })}
        >
          <Input
            id="password"
            type="password"
            autoComplete={invitation.accountExists ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={fieldErrors.password ? true : undefined}
            aria-describedby={fieldErrors.password || !invitation.accountExists ? fieldMessageId('password') : undefined}
          />
        </Field>
        {error ? <ErrorState message={error} /> : null}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          Accept invitation
        </Button>
      </form>
    </AuthShell>
  )
}
